import { useEffect, useMemo, useRef, useState } from 'react'
import { type ImageCache } from './types'
import { type LayoutNode } from '@backend/lib/db/typeRoots/layoutNode'
import drawCanvas from './drawCanvas'
import drawHandles, { drawVirtualBorder } from './drawHandles'
import { CanvasTextEditor } from './lexical/CanvasTextEditor'
import { rewrapFromContent } from '@/lib/rewrapTextNodesFromRuns'
import {
  getCursorStyle,
  type CanvasView,
  type MouseAction,
  onMouseDown,
  onMouseMove,
  selectItem,
  type SnapLine,
  getNodesInMarquee,
} from './interactions'
import { type Color } from '@backend/lib/db/typeRoots/color'
import { useNodeStore, getGroupMembers } from '@/stores/nodeStore'
import { useCanvasStore } from '@/stores/canvasStore'

type Props = {
  width: number
  height: number
  backgroundColor: Color
  panX: number
  panY: number
  zoom: number
  overlayOpacity?: number
  // NOTE: used to prevent setting 'hoveredNodeIndex' when quickmenu is open
  isHoveringQuickmenu: boolean
  setIsHoveringQuickmenu: (isHovering: boolean) => void
  onImageCached?: (ready: boolean) => void
}

export const CanvasEditor = ({
  width,
  height,
  backgroundColor,
  panX,
  panY,
  zoom,
  overlayOpacity = 0.12,
  isHoveringQuickmenu,
  setIsHoveringQuickmenu,
  onImageCached,
}: Props) => {
  const dpr = window.devicePixelRatio || 1

  const {
    nodes,
    selectedNodeIndices,
    selectedNodeIndex: _selectedNodeIndex,
    updateNode,
    setSelectedNodeIndex,
    setSelectedNodeIndices,
    addToSelection,
    toggleSelection,
    clearSelection,
    addHistoryStep,
  } = useNodeStore()

  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null)

  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })

  const [nodesBeforeAction, setNodesBeforeAction] = useState<
    Map<number, Omit<LayoutNode, 'data' | 'visible'>>
  >(new Map())
  const [mouseAction, setMouseAction] = useState<MouseAction>({ type: 'none', index: -1 })
  const [hoveredNodeIndex, setHoveredNodeIndex] = useState(-1)
  const [marquee, setMarquee] = useState<
    { start: { x: number; y: number }; current: { x: number; y: number } } | undefined
  >(undefined)

  const [imageCache, setImageCache] = useState<ImageCache>({})
  const [imageCacheLoaded, setImageCacheLoaded] = useState(true)

  const [snapLines, setSnapLines] = useState<SnapLine[]>([])

  // Holding Cmd/Ctrl enters a transient "detail mode" — snap is disabled
  // (see interactions.ts onMouseMove) and selection chrome is hidden so the
  // user can position pixel-perfectly without handle overlap. Tracked at the
  // editor level (not just during drag) so a hover-and-press also clears the
  // chrome, and so a release outside a drag restores it.
  const [isDetailModeActive, setIsDetailModeActive] = useState(false)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) setIsDetailModeActive(true)
    }
    const onKeyUp = (e: KeyboardEvent) => {
      if (!e.metaKey && !e.ctrlKey) setIsDetailModeActive(false)
    }
    // Tab-away can leave the modifier "stuck" — keyup never fires if focus
    // moved before the user lifted Cmd. Reset on blur to recover.
    const onBlur = () => setIsDetailModeActive(false)
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onBlur)
    }
  }, [])

  const [currentlyEditedNodeIndex, setCurrentlyEditedNodeIndex] = useState(-1)

  const { setCanvas, setCaptureFunction } = useCanvasStore()

  const view: CanvasView = useMemo(
    () => ({ panX, panY, zoom, virtualWidth: width, virtualHeight: height }),
    [panX, panY, zoom, width, height],
  )

  // Resize canvas to fill container
  useEffect(() => {
    if (!containerRef.current) return
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) {
        setContainerSize({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        })
      }
    })
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  // Initialize canvas context
  useEffect(() => setCtx(canvasRef.current?.getContext('2d') || null), [])

  // FPS counter (dev only — dynamically imported to avoid bundling in production)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const statsRef = useRef<any>(null)
  useEffect(() => {
    if (import.meta.env.PROD || !containerRef.current) return
    const container = containerRef.current
    let cancelled = false
    import('stats.js').then(({ default: Stats }) => {
      if (cancelled) return
      const stats = new Stats()
      stats.showPanel(0)
      stats.dom.style.position = 'absolute'
      stats.dom.style.top = '0'
      stats.dom.style.left = '0'
      stats.dom.style.zIndex = '9999'
      container.appendChild(stats.dom)
      statsRef.current = stats
    })
    return () => {
      cancelled = true
      if (statsRef.current) {
        statsRef.current.dom.remove()
        statsRef.current = null
      }
    }
  }, [])

  // Update image cache
  useEffect(() => {
    const imagesToLoad = nodes
      .map((node) => node.data)
      .filter((data) => data.type === 'image')
      .filter(({ source }) => {
        const cacheKey = source.url
        return !Object.keys(imageCache).includes(cacheKey)
      })

    if (imagesToLoad.length === 0) {
      // If no images to load, we're already ready
      if (onImageCached) onImageCached(true)
      return
    }

    setImageCacheLoaded(false)
    if (onImageCached) onImageCached(false)

    const imagePromises = imagesToLoad.map(async ({ source }) => {
      if (source.type === 'url') {
        const img = new Image()
        img.crossOrigin = 'anonymous' // Must be set before src to include Origin header
        img.src = source.url
        const loaded = await new Promise<boolean>((resolve) => {
          img.onload = () => resolve(true)
          img.onerror = () => resolve(false)
        })
        if (!loaded) {
          console.warn('Failed to load image:', source.url)
          return null
        }
        return { cacheKey: source.url, img }
      }

      throw new Error('Unknown source type')
    })

    Promise.all(imagePromises)
      .then((results) => {
        const loadedImages = results.filter(
          (r): r is { cacheKey: string; img: HTMLImageElement } => r !== null,
        )

        if (loadedImages.length > 0) {
          const newImageCache = loadedImages.reduce(
            (acc, { cacheKey, img }) => {
              acc[cacheKey] = img
              return acc
            },
            {} as Record<string, HTMLImageElement>,
          )

          setImageCache((prevCache) => ({
            ...prevCache,
            ...newImageCache,
          }))
        }
        console.log('Image cache loaded')
        setImageCacheLoaded(true)
        if (onImageCached) onImageCached(true)
      })
      .catch((error) => {
        console.error('Error loading images:', error)
        if (onImageCached) onImageCached(true)
      })
  }, [nodes, imageCache, onImageCached])

  // Positioning, focus, and content-edit wiring all live inside
  // <CanvasTextEditor> — see ./lexical/CanvasTextEditor.tsx.

  // Fonts load asynchronously (Google fonts on startup, moodboard custom fonts
  // once registered). The canvas 2D context paints with whatever is loaded at
  // draw time and ignores font-display, so bump a version on each `loadingdone`
  // to force a redraw — otherwise a node using a not-yet-loaded family stays in
  // the fallback face until the next unrelated interaction.
  const [fontsVersion, setFontsVersion] = useState(0)
  useEffect(() => {
    const onLoadingDone = () => setFontsVersion((v) => v + 1)
    document.fonts.addEventListener('loadingdone', onLoadingDone)
    return () => document.fonts.removeEventListener('loadingdone', onLoadingDone)
  }, [])

  // Redraw canvas
  useEffect(() => {
    if (!ctx) return
    if (containerSize.width === 0 || containerSize.height === 0) return

    // Defer drawing to next animation frame — batches multiple state changes into one draw
    const frameId = requestAnimationFrame(() => {
      statsRef.current?.begin()

      // Clear entire physical canvas
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, containerSize.width, containerSize.height)

      // Apply pan/zoom transform — all subsequent drawing is in node-space
      ctx.translate(panX, panY)
      ctx.scale(zoom, zoom)

      drawCanvas(backgroundColor, nodes, imageCache, ctx, width, height, overlayOpacity)
      if (!isDetailModeActive) {
        drawHandles(nodes, ctx, selectedNodeIndices, hoveredNodeIndex, snapLines, marquee)
      }
      if (overlayOpacity < 0.4) drawVirtualBorder(ctx, width, height)

      statsRef.current?.end()
    })

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button === 1) {
        e.preventDefault()
        return
      }

      if (!canvasRef.current) return
      if (!ctx) return

      // NOTE: used to reset this state when adding a new node
      setIsHoveringQuickmenu(false)

      // NOTE: used to trigger an update on the input field when selecting a different node
      if (document.activeElement instanceof HTMLInputElement) {
        document.activeElement.blur()
      }

      const newMouseAction = onMouseDown(
        e,
        canvasRef.current,
        ctx,
        nodes,
        selectedNodeIndices,
        view,
      )

      // Handle shift+click for multi-selection
      if (
        'shiftClick' in newMouseAction &&
        newMouseAction.shiftClick &&
        newMouseAction.clickedIndex !== undefined
      ) {
        // Expand selection to include group members if applicable
        const groupMembers = getGroupMembers(nodes, newMouseAction.clickedIndex)
        groupMembers.forEach((idx) => toggleSelection(idx))
        return
      }

      // Handle marquee start
      if (newMouseAction.type === 'marquee') {
        setMarquee(newMouseAction)
        setMouseAction(newMouseAction)
        // Clear selection when starting marquee (unless shift is held)
        if (!e.shiftKey) {
          clearSelection()
        }
        return
      }

      // Handle clicking on a new node (not in current selection)
      let finalMouseAction = newMouseAction
      if ('clickedIndex' in newMouseAction && newMouseAction.clickedIndex !== undefined) {
        const clickedIndex = newMouseAction.clickedIndex
        // Expand selection to include group members
        const groupMembers = getGroupMembers(nodes, clickedIndex)
        setSelectedNodeIndices(groupMembers)

        // If the node is part of a group and we're starting a drag,
        // convert to multi-drag so the entire group moves together
        if (groupMembers.length > 1 && newMouseAction.type === 'drag') {
          const mouse = {
            x: nodes[clickedIndex]!.x + newMouseAction.offset.x,
            y: nodes[clickedIndex]!.y + newMouseAction.offset.y,
          }
          const offsets = groupMembers.map((i) => ({
            x: mouse.x - nodes[i]!.x,
            y: mouse.y - nodes[i]!.y,
          }))
          finalMouseAction = { type: 'multi-drag', indices: groupMembers, offsets }
        }
      }

      setMouseAction(finalMouseAction)

      // Store initial node states for undo comparison
      if (
        finalMouseAction.type === 'drag' ||
        finalMouseAction.type === 'resize' ||
        finalMouseAction.type === 'rotate'
      ) {
        const newMap = new Map<number, Omit<LayoutNode, 'data' | 'visible'>>()
        const node = nodes[finalMouseAction.index]!
        newMap.set(finalMouseAction.index, {
          x: node.x,
          y: node.y,
          width: node.width,
          height: node.height,
          rotation: node.rotation,
        })
        setNodesBeforeAction(newMap)
      } else if (
        finalMouseAction.type === 'multi-drag' ||
        finalMouseAction.type === 'multi-resize'
      ) {
        const indices = 'indices' in finalMouseAction ? finalMouseAction.indices : []
        const newMap = new Map<number, Omit<LayoutNode, 'data' | 'visible'>>()
        indices.forEach((i) => {
          const node = nodes[i]!
          newMap.set(i, {
            x: node.x,
            y: node.y,
            width: node.width,
            height: node.height,
            rotation: node.rotation,
          })
        })
        setNodesBeforeAction(newMap)
      }
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!canvasRef.current) return
      if (!ctx) return
      if (isHoveringQuickmenu) return

      const output = onMouseMove(
        e,
        canvasRef.current,
        ctx,
        mouseAction,
        nodes,
        view,
        selectedNodeIndices,
      )

      setHoveredNodeIndex(output.hoveredNodeIndex)

      canvasRef.current.style.cursor = getCursorStyle(mouseAction, output.selectedNodeControls)

      setSnapLines(output.snapLines || [])

      if (output.update) {
        // Handle single node update
        if ('node' in output.update && output.update.index !== undefined) {
          const idx = output.update.index
          updateNode(idx, withRewrappedTextLayout(nodes[idx], output.update.node, ctx))
        }
        // Handle multi-node update
        if ('multiNodes' in output.update && output.update.multiNodes) {
          output.update.multiNodes.forEach(({ index, node }) => {
            updateNode(index, withRewrappedTextLayout(nodes[index], node, ctx))
          })
        }
        // Handle action update (corner/border switch during resize)
        if ('action' in output.update && output.update.action) {
          const actionUpdate = output.update.action
          setMouseAction((prev) => {
            if (prev.type === 'resize') {
              return {
                ...prev,
                corner: actionUpdate.corner,
                border: actionUpdate.border,
              }
            }
            return prev
          })
        }
        // Handle marquee update
        if ('marquee' in output.update && output.update.marquee) {
          const marqueeUpdate = output.update.marquee
          setMarquee(marqueeUpdate)
          if (marqueeUpdate.start && marqueeUpdate.current) {
            setMouseAction({
              type: 'marquee',
              start: marqueeUpdate.start,
              current: marqueeUpdate.current,
            })
          }
        }
      }
    }

    ctx.canvas.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('mousemove', handleMouseMove)

    return () => {
      cancelAnimationFrame(frameId)
      ctx.canvas.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mousemove', handleMouseMove)
    }
  }, [
    nodes,
    updateNode,
    setSelectedNodeIndex,
    setSelectedNodeIndices,
    addToSelection,
    toggleSelection,
    clearSelection,
    selectedNodeIndices,
    backgroundColor,
    imageCache,
    imageCacheLoaded,
    ctx,
    mouseAction,
    hoveredNodeIndex,
    dpr,
    panX,
    panY,
    zoom,
    width,
    height,
    containerSize,
    snapLines,
    marquee,
    overlayOpacity,
    isHoveringQuickmenu,
    setIsHoveringQuickmenu,
    view,
    isDetailModeActive,
    fontsVersion,
  ])

  // Keep the mouse action in sync with the primary selected index
  const [prevSelectedNodeIndices, setPrevSelectedNodeIndices] = useState(selectedNodeIndices)
  if (prevSelectedNodeIndices !== selectedNodeIndices) {
    setPrevSelectedNodeIndices(selectedNodeIndices)
    if (selectedNodeIndices.length > 0 && mouseAction.type === 'none') {
      setMouseAction({ ...mouseAction, index: selectedNodeIndices[0]! })
    }
  }

  // update canvas in store
  useEffect(() => {
    setCanvas('main', canvasRef.current)
    return () => setCanvas('main', null)
  }, [canvasRef, setCanvas])

  // Register capture function — uses ref to always capture latest state without re-render cycles
  const captureRef = useRef<() => Promise<File>>(null!)
  useEffect(() => {
    captureRef.current = async () => {
      const offscreen = document.createElement('canvas')
      offscreen.width = width * dpr
      offscreen.height = height * dpr
      const offCtx = offscreen.getContext('2d')!
      offCtx.setTransform(dpr, 0, 0, dpr, 0, 0)
      drawCanvas(backgroundColor, nodes, imageCache, offCtx, width, height)
      return new Promise<File>((resolve, reject) => {
        offscreen.toBlob(
          (blob) =>
            blob
              ? resolve(new File([blob], 'canvas.png', { type: 'image/png' }))
              : reject(new Error('Failed to create blob')),
          'image/png',
        )
      })
    }
  }) // runs after every render to keep ref current

  useEffect(() => {
    setCaptureFunction('main', () => captureRef.current())
    return () => setCaptureFunction('main', null)
  }, [setCaptureFunction])

  // mouse event handlers

  const handleMouseUp = (shiftKey = false) => {
    // Handle marquee selection completion
    if (mouseAction.type === 'marquee') {
      const { start, current } = mouseAction
      const marqueeIndices = getNodesInMarquee(nodes, start, current)

      if (marqueeIndices.length > 0) {
        // Expand to full groups so a marquee never splits a group.
        const boxed = new Set<number>()
        marqueeIndices.forEach((idx) => {
          getGroupMembers(nodes, idx).forEach((memberIdx) => boxed.add(memberIdx))
        })
        if (shiftKey) {
          // Shift-release toggles each boxed node against the current selection:
          // unselected ones get added, already-selected ones get removed.
          const next = new Set(selectedNodeIndices)
          boxed.forEach((i) => (next.has(i) ? next.delete(i) : next.add(i)))
          setSelectedNodeIndices(Array.from(next))
        } else {
          setSelectedNodeIndices(Array.from(boxed))
        }
      }

      setMarquee(undefined)
      setMouseAction({ type: 'none', index: -1 })
      return
    }

    // Check if any nodes changed for history
    if (
      mouseAction.type === 'drag' ||
      mouseAction.type === 'resize' ||
      mouseAction.type === 'rotate'
    ) {
      const index = mouseAction.index
      const before = nodesBeforeAction.get(index)
      const current = nodes[index]
      if (before && current) {
        const hasChanged =
          before.x !== current.x ||
          before.y !== current.y ||
          before.width !== current.width ||
          before.height !== current.height ||
          before.rotation !== current.rotation
        if (hasChanged) {
          addHistoryStep()
        }
      }
    } else if (mouseAction.type === 'multi-drag' || mouseAction.type === 'multi-resize') {
      let hasAnyChanged = false
      const indices = mouseAction.indices
      for (const index of indices) {
        const before = nodesBeforeAction.get(index)
        const current = nodes[index]
        if (before && current) {
          const hasChanged =
            before.x !== current.x ||
            before.y !== current.y ||
            before.width !== current.width ||
            before.height !== current.height
          if (hasChanged) {
            hasAnyChanged = true
            break
          }
        }
      }
      if (hasAnyChanged) {
        addHistoryStep()
      }
    }

    const primaryIndex = selectedNodeIndices.length > 0 ? selectedNodeIndices[0]! : -1
    setMouseAction({ type: 'none', index: primaryIndex })
  }

  const handleDoubleClick = (e: MouseEvent) => {
    if (!canvasRef.current) return
    if (!ctx) return

    e.preventDefault()

    const primaryIndex = selectedNodeIndices.length > 0 ? selectedNodeIndices[0]! : -1
    const { index } = selectItem(e, canvasRef.current, ctx, nodes, primaryIndex, view)

    const node = nodes[index]
    if (!node || node.data.type !== 'text') return

    setCurrentlyEditedNodeIndex(index)
    updateNode(index, { data: { editMode: true } })
  }

  const editedNode = currentlyEditedNodeIndex !== -1 ? nodes[currentlyEditedNodeIndex] : undefined
  const editedTextNode =
    editedNode && editedNode.data.type === 'text'
      ? (editedNode as LayoutNode & { data: Extract<LayoutNode['data'], { type: 'text' }> })
      : undefined

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
      <canvas
        ref={canvasRef}
        width={containerSize.width * dpr}
        height={containerSize.height * dpr}
        style={{ width: containerSize.width, height: containerSize.height }}
        onDoubleClick={(e: React.MouseEvent<HTMLCanvasElement>) => handleDoubleClick(e.nativeEvent)}
        onMouseUp={(e) => handleMouseUp(e.shiftKey)}
      />

      {editedTextNode && ctx && (
        <CanvasTextEditor
          key={currentlyEditedNodeIndex}
          node={editedTextNode}
          nodeIndex={currentlyEditedNodeIndex}
          panX={panX}
          panY={panY}
          zoom={zoom}
          ctx={ctx}
          onCommit={() => setCurrentlyEditedNodeIndex(-1)}
        />
      )}
    </div>
  )
}

/** When a resize/move update changes a text node's width, rewrap from the
 *  current run array so per-run styles (and the captured line-height) carry
 *  into the new geometry. Without this, the store's invalidation rule clears
 *  preWrappedLines and the legacy painter collapses every run to the
 *  node-level effective style — losing colors, weights, and inline italic. */
const withRewrappedTextLayout = (
  prevNode: LayoutNode | undefined,
  updates: NestedPartial<LayoutNode>,
  ctx: CanvasRenderingContext2D | null,
): NestedPartial<LayoutNode> => {
  if (!prevNode || prevNode.data.type !== 'text' || !ctx) return updates
  const widthChanged = updates.width != null && updates.width !== prevNode.width
  // Height-only changes don't affect line wrapping but DO affect vertical
  // alignment — center/bottom-aligned text needs to re-anchor against the
  // new inner box. Trigger a rewrap in that case too so the captured
  // line.y values stay consistent with the new geometry.
  const heightAffectsLayout =
    updates.height != null &&
    updates.height !== prevNode.height &&
    prevNode.data.options.verticalAlign !== 'top'
  if (!widthChanged && !heightAffectsLayout) return updates
  const liveNode = {
    ...prevNode,
    ...updates,
    data: prevNode.data,
  } as LayoutNode & { data: Extract<LayoutNode['data'], { type: 'text' }> }
  const fresh = rewrapFromContent(liveNode, ctx)
  if (!fresh) return updates
  return {
    ...updates,
    data: { options: { preWrappedLines: fresh } },
  }
}
